import React from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import { Box, Grid, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import EditPencil from '../../../assets/EditPencil.svg';
import FormCloseButton from '../../../components/Form/FormCloseButton';
import { DashboardLoading, NoDataState } from '../../../components/LoadingStates';
import ScoreChart from '../../../components/ScoreChart';
import TextLink from '../../../components/TextLink/TextLink';
import selectUser from '../../../store/selectors/appSelector';
import { PRIMARY_PURPLE } from '../../../theme/colorConstants';
import useFairReliabilityScore from '../../../utils/useFairReliabilityScore';
import { ROUTE_CONSTANTS } from '../../app/RouteBuilding/routeConstants';
import DashboardJobs from '../DashboardJobs';
import DashboardOnboarding from '../DashboardOnboarding';
import { useDashboardData, useDashboardPermissions, useDashboardRendering } from '../hooks';
import { DashboardWidgets } from '../shared';

import useAdminAgencyDataFetching from './hooks/useAdminAgencyDataFetching';

/**
 * AdminAgencySpecificDashboard component for admin viewing specific agency data
 * - Handles admins going to /dashboard/agency/:id
 * - Displays agency-specific widgets and data for the selected agency
 */
const AdminAgencySpecificDashboard = () => {
  const theme = useTheme();
  const user = useSelector(selectUser);
  const { pickReliability } = useFairReliabilityScore();
  const location = useLocation();
  const navigate = useNavigate();
  const { pathSelectedAgencyId, isAdmin } = useDashboardPermissions(user);

  // Admin-specific data fetching for the selected agency
  const { agencyDetail, isGetAgencyLoading } = useAdminAgencyDataFetching(pathSelectedAgencyId);

  const { revenueLabels, workers, revenue, futurePlacementsData } = useDashboardData(
    user,
    null, // No widget data for agency-specific dashboards
    agencyDetail,
    true // Always agency dashboard
  );

  const { isLoading, shouldShowWidgets, shouldShowNoData } = useDashboardRendering(
    false, // No widget data loading for agency-specific dashboards
    isGetAgencyLoading,
    true, // Always agency dashboard
    agencyDetail,
    null // No widget data
  );

  if (isLoading) {
    return <DashboardLoading />;
  }

  return (
    <Box
      data-testid="admin-dashboard"
      padding={theme.components.dashboard.layout.padding}
      display="flex"
      flexDirection="column"
      gap={theme.components.dashboard.layout.sectionGap}
    >
      {/* Header with back button and edit option */}
      {pathSelectedAgencyId && (
        <Grid container item direction="row" sx={{ justifyContent: 'space-between' }}>
          <FormCloseButton
            onClose={() => navigate(`/${ROUTE_CONSTANTS.DASHBOARD}${location.search}`)}
            message="buttonText.goBack"
          />
          {isAdmin && (
            <TextLink
              to={
                pathSelectedAgencyId
                  ? `/${ROUTE_CONSTANTS.DASHBOARD_AGENCY_EDIT}/${pathSelectedAgencyId}`
                  : undefined
              }
              sx={{
                fontFamily: 'Roboto-400',
                color: PRIMARY_PURPLE[70],
                fontSize: 20,
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              {LanguageConverterV2('dashboard.agency.editAgencyDetail')}
              <Box
                component="img"
                sx={{ height: 15, width: 15, marginLeft: 1 }}
                alt="Edit Agency"
                src={EditPencil}
              />
            </TextLink>
          )}
        </Grid>
      )}

      {/* Agency Header */}
      <Stack>
        <Stack direction="row" justifyContent="space-between" flexWrap="wrap" alignItems="center">
          <Box>
            <Typography sx={theme.components.dashboard.typography.header}>
              {agencyDetail?.name}
            </Typography>
            <Typography sx={theme.components.dashboard.typography.subHeader}>
              {LanguageConverterV2('dashboard.agency.code')} {agencyDetail?.onboardingId}
            </Typography>
          </Box>
          {workers && (
            <ScoreChart
              label={LanguageConverterV2('dashboard.widget.reliability.title')}
              score={pickReliability(
                workers.averageRawReliability,
                workers.averageReliabilityScore
              )}
              description={LanguageConverterV2('dashboard.widget.reliability.description')}
            />
          )}
        </Stack>
        <Stack direction="row">
          <DashboardOnboarding />
        </Stack>
      </Stack>

      {/* Agency Dashboard Widgets */}
      <DashboardWidgets
        isLoading={isLoading}
        workers={workers}
        revenue={revenue}
        revenueLabels={revenueLabels}
        futurePlacementsData={futurePlacementsData}
        showWidgets={shouldShowWidgets}
      />

      {/* No Data State */}
      {shouldShowNoData && <NoDataState />}

      {/* Agency-specific content */}
      <DashboardJobs agencyId={pathSelectedAgencyId} />
    </Box>
  );
};

export default AdminAgencySpecificDashboard;
